import { Analytics } from '@cobliteam/analytics';
import { useTranslation } from '@cobliteam/i18n';
import DropdownMenu from '@cobliteam/ui/components/DropdownMenu';
import type { Table } from '@tanstack/react-table';
import DataTableExport from '../DataTableExport';
import buildExcel, { type TExportType } from './spreadsheet';

export interface ExportProps<TData> {
  exportDef: ExportDef<TData>;
  data: TData[];
  table: Table<TData>;
  defaultOpen?: boolean;
  exporting?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
}

type IOnHandleExportProps = {
  filter?: boolean;
  exportType?: TExportType;
  copy?: boolean;
};

export type ExportDef<T> = {
  title: string;
  fileName: string;
  data: ExportDefData<T>[];
};

export type ExportDefData<T> = {
  id: string;
  accessorKey?: keyof T;
  columnName?: string;
  row?: (row: T) => string;
};

const Export = <TData,>({ table, data, exportDef, defaultOpen = false, disabled = false, open, onOpenChange, exporting = false }: ExportProps<TData>) => {
  const { t } = useTranslation('ui-business');
  const { globalFilter, sorting, columnFilters } = table.getState();

  // check if has filter apply
  const hasFilterApply = globalFilter || !!columnFilters.length || !!sorting.length;

  // get all columns from table
  const allColumns = table.getAllColumns().map((column) => column);

  // get all rows from table
  const allRows = table.getRowModel().rows.map((row) => row.original);

  // get columns from exportDef
  const columns = exportDef.data.map((item) => {
    const column = allColumns.find((c) => c.id === item.id);
    return item?.columnName || column?.columnDef?.meta?.name || t('data_table.no_column_name');
  });

  // get rows from exportDef
  const dataRows = () =>
    data.map((row) =>
      exportDef.data.map((item) => {
        if (item.row) return item.row(row);
        return item.accessorKey ? row[item.accessorKey] : '';
      })
    );

  // get rows from table filtered and apply exportDef
  const tableRows = () =>
    allRows.map((original) => {
      return exportDef.data.map((item) => {
        if (item.row) return item.row(original);
        return item.accessorKey ? original[item.accessorKey] : '';
      });
    });

  const onHandleExport = (params?: IOnHandleExportProps) => {
    const { exportType = 'xlsx', copy = false, filter: filtered } = params || {};

    const rows = filtered ? tableRows() : dataRows();

    Analytics.track('DataTable Exported', { exportType });

    buildExcel({
      data,
      rows,
      columns,
      copy,
      exportType,
      title: exportDef.title || t('data_table.exported_data'),
      fileName: exportDef.fileName || t('data_table.exported_data_filename'),
    });
  };

  return (
    <DataTableExport defaultOpen={defaultOpen} open={open} onOpenChange={onOpenChange} exporting={exporting}>
      <DropdownMenu.Item onClick={() => onHandleExport()}>
        <DropdownMenu.Label>{t('data_table.export_all_data_excel')}</DropdownMenu.Label>
      </DropdownMenu.Item>
      <DropdownMenu.Item onClick={() => onHandleExport({ exportType: 'csv' })}>
        <DropdownMenu.Label>{t('data_table.export_all_data_csv')}</DropdownMenu.Label>
      </DropdownMenu.Item>
      <DropdownMenu.Separator />

      <DropdownMenu.Item onClick={() => onHandleExport({ filter: true })} disabled={!hasFilterApply}>
        <DropdownMenu.Label>{t('data_table.export_filtered_data_excel')}</DropdownMenu.Label>
      </DropdownMenu.Item>
      <DropdownMenu.Item onClick={() => onHandleExport({ filter: true, exportType: 'csv' })} disabled={!hasFilterApply}>
        <DropdownMenu.Label>{t('data_table.export_filtered_data_csv')}</DropdownMenu.Label>
      </DropdownMenu.Item>
      <DropdownMenu.Separator />
      <DropdownMenu.Item onClick={() => onHandleExport({ copy: true, exportType: 'csv' })}>
        <DropdownMenu.Label>{t('data_table.copy_table_csv')}</DropdownMenu.Label>
      </DropdownMenu.Item>
    </DataTableExport>
  );
};

export default Export;
